import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {DraggerIcon, TrashIcon} from "assets/icons";
import ProfileImage from "components/ProfileImage";
import {ICandidate} from "api/types";
import {useState} from "react";
import {useNavigate} from "react-router";
import useAwaitableModal from "hooks/useAwaitableModal";
import Feedback from "modals/Feedback";
import accountantApi from "api/accountant";
import Tooltip from "components/Tooltip";

interface Props {
  proposal: ICandidate;
  deleteTask: (id: string) => void;
  changeProposalNote: (id: string, name: string) => void;
  disabled?: any;
}

function ProposalCard({proposal, deleteTask, disabled, changeProposalNote}: Props) {
  const {setNodeRef, attributes, listeners, transform, transition, isDragging} = useSortable({
    id: proposal.id,
    data: {type: "Proposal", proposal},
    disabled: !!disabled,
  });
  const [feedback, setFeedback] = useState(proposal.feedback);
  const navigate = useNavigate();
  const [leaveFeedback, FeedbackModal] = useAwaitableModal(Feedback, {} as any);
  const [requesting, setRequesting] = useState(false);
  const [note, setNote] = useState(proposal.note || "");

  const canLeaveFeedback = proposal.state === "done" && !feedback && false;

  const onLeaveFeedback = (e: any) => {
    e.stopPropagation();
    if (!canLeaveFeedback) return;
    leaveFeedback({id: proposal.id, name: proposal.name, image: proposal.image}).then(() => setFeedback(true));
  };

  const onDelete = (e: any) => {
    e.stopPropagation();
    if (requesting) return;
    setRequesting(true);
    accountantApi
      .deleteCandidate(proposal.id)
      .then(() => deleteTask(proposal.id))
      .finally(() => setRequesting(false));
  };

  const style = {transition, transform: CSS.Transform.toString(transform)};

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={["flex gap-2 items-start", isDragging ? "opacity-40" : "opacity-100"].asClass}
    >
      {FeedbackModal}
      <DraggerIcon
        className={["cursor-grab mt-3 text-brand-muted shrink-0 outline-none", isDragging && "opacity-0"].asClass}
        {...attributes}
        {...listeners}
      />
      <div
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/recruiter/${proposal.id}`);
        }}
        className={
          [
            "flex-col flex border border-brand-border bg-white rounded-xl p-4 gap-3",
            "cursor-pointer transition-shadow hover:shadow-sm grow select-none",
          ].asClass
        }
        style={{boxShadow: "rgba(51,41,28,0.04) 0px 1px 4px"}}
      >
        {/* Header row */}
        <div className="flex items-center gap-3">
          <ProfileImage size={40} src={proposal.image} />
          <div className="flex flex-col grow min-w-0">
            <span className="text-sm font-semibold text-brand-navy truncate select-none">{proposal.name}</span>
            {canLeaveFeedback && (
              <span
                className="text-brand-amber text-xs font-medium hover:underline cursor-pointer"
                onClick={onLeaveFeedback}
              >
                Leave Feedback
              </span>
            )}
          </div>
          <Tooltip position="left" text="Remove">
            <button
              onClick={onDelete}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-brand-cream text-brand-muted hover:text-brand-navy transition-colors cursor-pointer"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>

        {/* Note */}
        <div className="border-t border-brand-border pt-3 flex flex-col gap-2">
          <span className="text-xs text-brand-muted">Note</span>
          <input
            value={note}
            onClick={(e) => e.stopPropagation()}
            className="!border-0 !rounded-none !p-0 text-xs outline-none w-full text-brand-navy placeholder:text-brand-muted bg-transparent"
            placeholder="Add a note..."
            onChange={(e) => setNote(e.currentTarget.value)}
            onBlur={(e) => changeProposalNote(proposal.id, e.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  );
}

export default ProposalCard;
